import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Loader2, Info, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const N8N_WEBHOOK_URL = process.env.REACT_APP_N8N_WEBHOOK_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const TSPM_API = "https://tspm-api.abrandfantastic.workers.dev";

const CMS_DEFAULTS = {
  lol_headline:     "Should Lollipop make a come back?",
  lol_subtext:      "It once meant something.\nI'm thinking about bringing it back\nbut only if you want me to.",
  lol_video:        "https://cdn.houseoflollipop.com/video/v1769181591/DJMon.mp4",
  lol_video_badge:  "LOLLIPOP",
  lol_video_caption:"DJ Mon on the ones and twos",
  lol_submit_btn:   "Yes. Bring it Back",
  lol_privacy_note: "No spam. Just updates if Lollipop returns.",
};

export default function WaitlistPage() {
  const [cms, setCms] = useState(CMS_DEFAULTS);

  useEffect(() => {
    fetch(`${TSPM_API}/api/site-content`)
      .then(r => r.json())
      .then(data => setCms(c => ({ ...c, ...Object.fromEntries(Object.keys(CMS_DEFAULTS).map(k => [k, data[k] ?? c[k]])) })))
      .catch(() => {});
  }, []);
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [birthday, setBirthday] = useState("");
  const [dateFormat, setDateFormat] = useState("UK"); // UK: DD/MM/YYYY, US: MM/DD/YYYY
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBirthdayInfo, setShowBirthdayInfo] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  // Hidden tracking fields
  const [trackingData, setTrackingData] = useState({
    source: "",
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    ref: "",
    page_url: "",
    hp: "" // honeypot field
  });

  // Extract UTM parameters and tracking data on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let source = 'direct';
    try {
      source = params.get('source') || (document.referrer ? new URL(document.referrer).hostname : 'direct');
    } catch (e) {
      source = 'direct';
    }
    setTrackingData({
      source,
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_content: params.get('utm_content') || '',
      ref: params.get('ref') || '',
      page_url: window.location.href,
      hp: ''
    });
  }, []);

  // Render Turnstile widget
  useEffect(() => {
    if (turnstileRef.current && window.turnstile && TURNSTILE_SITE_KEY) {
      window.turnstile.render(turnstileRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        'refresh-expired': 'auto',
        callback: (token) => {
          setTurnstileToken(token);
        },
        'error-callback': () => {
          setTurnstileToken('');
        },
      });
    }
  }, []);

  const handleBirthdayChange = (e) => {
    let value = e.target.value.replace(/[^\d]/g, '');
    
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (value.length >= 5) {
      value = value.slice(0, 5) + '/' + value.slice(5, 9);
    }
    
    setBirthday(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!firstName.trim() || !email.trim()) {
      toast.error("Please fill in required fields");
      return;
    }

    // Anti-spam: if honeypot is filled, silently reject
    if (trackingData.hp) {
      setIsSubmitting(false);
      return;
    }

    // Check Turnstile token
    if (!turnstileToken) {
      toast.error("Please complete the security check");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(N8N_WEBHOOK_URL, {
        // Form fields
        first_name: firstName,
        surname: surname || null,
        email,
        birthday: birthday || null,
        date_format: dateFormat,

        // Tracking fields
        source: trackingData.source,
        utm_source: trackingData.utm_source,
        utm_medium: trackingData.utm_medium,
        utm_campaign: trackingData.utm_campaign,
        utm_content: trackingData.utm_content,
        ref: trackingData.ref,
        page_url: trackingData.page_url,
        hp: trackingData.hp,

        // Additional metadata
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),

        // Turnstile token for verification
        cf_turnstile_response: turnstileToken
      });

      navigate("/thank-you", { state: { name: firstName } });
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="waitlist-page" data-testid="waitlist-page">
      {/* Top Marquee - matching shop style */}
      <div className="top-marquee" data-testid="top-marquee">
        <div className="marquee-track">
          {[...Array(10)].map((_, i) => (
            <span key={i} className="marquee-item">
              VINTAGE TEES FROM THE VAULT &nbsp; ✦ &nbsp; Lollipop + Popsicle &nbsp; ✦ &nbsp; FREE SHIPPING ON ORDERS OVER £75 &nbsp; ✦ &nbsp;
            </span>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="waitlist-main">
        <div className="waitlist-container">
          {/* Video Section - Product card style */}
          <motion.div 
            className="video-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="video-section"
          >
            <div className="video-wrapper">
              <video
                key={cms.lol_video}
                className="video-player"
                src={cms.lol_video}
                autoPlay
                loop
                muted={true}
                playsInline
                data-testid="waitlist-video"
              />
            </div>
            <div className="video-label">
              <span className="label-tag">{cms.lol_video_badge}</span>
            </div>
            <div className="video-caption">
              {cms.lol_video_caption}
            </div>
          </motion.div>

          {/* Form Section */}
          <motion.div 
            className="form-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            data-testid="form-section"
          >
            <h1 className="headline" data-testid="headline">
              {cms.lol_headline}
            </h1>

            <p className="subtext" data-testid="subtext">
              {cms.lol_subtext.split('\n').map((line, i) => (
                <span key={i}>{line}{i < cms.lol_subtext.split('\n').length - 1 && <br />}</span>
              ))}
            </p>

            <form className="signup-form" onSubmit={handleSubmit} data-testid="signup-form">
              {/* Honeypot field - hidden from humans, visible to bots */}
              <input
                type="text"
                name="hp"
                value={trackingData.hp}
                onChange={(e) => setTrackingData({...trackingData, hp: e.target.value})}
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              <div className="form-row">
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="First name *"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="form-input"
                    data-testid="firstname-input"
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="form-field">
                  <input
                    type="text"
                    placeholder="Surname"
                    value={surname}
                    onChange={(e) => setSurname(e.target.value)}
                    className="form-input"
                    data-testid="surname-input"
                    disabled={isSubmitting}
                  />
                </div>
              </div>
              
              <div className="form-field">
                <input
                  type="email"
                  placeholder="Email *"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  data-testid="email-input"
                  disabled={isSubmitting}
                />
              </div>

              <div className="form-field">
                <div className="birthday-label">
                  <span className="field-label">Birthday</span>
                  <button 
                    type="button" 
                    className="info-btn"
                    onClick={() => setShowBirthdayInfo(true)}
                    data-testid="birthday-info-btn"
                  >
                    <Info size={14} />
                  </button>
                </div>
                <div className="birthday-field">
                  <input
                    type="text"
                    placeholder={dateFormat === "UK" ? "DD/MM/YYYY" : "MM/DD/YYYY"}
                    value={birthday}
                    onChange={handleBirthdayChange}
                    className="form-input birthday-input"
                    data-testid="birthday-input"
                    disabled={isSubmitting}
                    maxLength={10}
                  />
                  <div className="date-format-switch" data-testid="date-format-switch">
                    <button
                      type="button"
                      className={`format-btn ${dateFormat === "UK" ? "active" : ""}`}
                      onClick={() => setDateFormat("UK")}
                      data-testid="uk-format-btn"
                    >
                      UK
                    </button>
                    <button
                      type="button"
                      className={`format-btn ${dateFormat === "US" ? "active" : ""}`}
                      onClick={() => setDateFormat("US")}
                      data-testid="us-format-btn"
                    >
                      US
                    </button>
                  </div>
                </div>
              </div>

              {/* Birthday Info Popup */}
              <AnimatePresence>
                {showBirthdayInfo && (
                  <motion.div 
                    className="popup-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={() => setShowBirthdayInfo(false)}
                    data-testid="birthday-popup-overlay"
                  >
                    <motion.div 
                      className="popup-card"
                      initial={{ opacity: 0, scale: 0.9, y: 20 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: 20 }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid="birthday-popup"
                    >
                      <button 
                        className="popup-close"
                        onClick={() => setShowBirthdayInfo(false)}
                        data-testid="popup-close-btn"
                      >
                        <X size={18} />
                      </button>
                      <div className="popup-icon">🎁</div>
                      <h3 className="popup-title">Why your birthday?</h3>
                      <p className="popup-text">
                        We'd love to send you a special gift code or maybe even a little present on your birthday. It's our way of saying thanks for being part of the Lollipop family.
                      </p>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Turnstile widget */}
              <div
                ref={turnstileRef}
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '20px 0'
                }}
              ></div>

              <motion.button
                type="submit"
                className="submit-btn"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    <span>Joining...</span>
                  </>
                ) : (
                  <>
                    <span>{cms.lol_submit_btn}</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </motion.button>
            </form>

            <p className="privacy-note">
              {cms.lol_privacy_note}
            </p>
          </motion.div>
        </div>
      </main>

      {/* Bottom brand bar */}
      <div className="bottom-bar" data-testid="bottom-bar">
        <div className="bottom-marquee">
          {[...Array(10)].map((_, i) => (
            <span key={i} className="bottom-item">
              LOLLIPOP + POPSICLE &nbsp; &nbsp;
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
